/**
 * Chooses which training experience a session gets.
 *
 * Exactly two modes exist:
 *   - knowledge-base tracks  -> TrainingSessionPage (KB read-out, port 6340)
 *   - playbook tracks        -> PlaybookLiveSessionPage (full duplex, 6342)
 *
 * There is no fallback from a playbook track to the turn-based page: that
 * legacy playbook mode is gone.
 *
 * If the track cannot be loaded we say so and offer a retry rather than
 * guessing: sending a playbook track to the KB page would start a session the
 * server can no longer run.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import PlaybookLiveSessionPage from './PlaybookLiveSessionPage';
import TrainingSessionPage from './TrainingSessionPage';
import { Button } from '../components/ui/button';
import { config } from '../config';
import { TRAINING_ROUTER_COPY } from '../constants/training';

export type TrainingRouteDecision =
  | 'deciding'
  | 'kb'
  | 'playbook_live'
  | 'load_failed';

const PLAYBOOK_TRACK_TYPE = 'playbook';

/** Pure routing rule, exported for tests. */
export function decideTrainingRoute(trackType: string | null | undefined): TrainingRouteDecision {
  return trackType === PLAYBOOK_TRACK_TYPE ? 'playbook_live' : 'kb';
}

interface NoticeProps {
  title: string;
  body: string;
  onRetry?: () => void;
}

const RouterNotice: React.FC<NoticeProps> = ({ title, body, onRetry }) => (
  <div className="min-h-screen flex items-center justify-center bg-surface-subtle px-4">
    <div role="alert" className="bg-surface border border-hairline rounded-card shadow-sm p-8 max-w-md w-full text-center">
      <h1 className="heading-2 mb-2">{title}</h1>
      <p className="text-ink-secondary mb-6">{body}</p>
      <div className="flex justify-center gap-2">
        {onRetry && (
          <Button type="button" variant="primary" onClick={onRetry}>
            {TRAINING_ROUTER_COPY.retry}
          </Button>
        )}
        <Button
          type="button"
          variant={onRetry ? 'secondary' : 'primary'}
          onClick={() => {
            window.location.href = `${config.server.adminPortalUrl}/training`;
          }}
        >
          {TRAINING_ROUTER_COPY.backToTraining}
        </Button>
      </div>
    </div>
  </div>
);

export const TrainingSessionRouter: React.FC = () => {
  const location = useLocation();
  const trackId = new URLSearchParams(location.search).get('trackId');
  const [decision, setDecision] = useState<TrainingRouteDecision>(trackId ? 'deciding' : 'kb');
  const [attempt, setAttempt] = useState(0);

  const handleRetry = useCallback(() => {
    setDecision('deciding');
    setAttempt((count) => count + 1);
  }, []);

  useEffect(() => {
    // No track to look up: the KB page owns that state (it cannot connect
    // without a trackId and shows its own start screen).
    if (!trackId) {
      setDecision('kb');
      return;
    }

    let isCancelled = false;
    const resolveRoute = async (): Promise<void> => {
      try {
        const { apiService } = await import('../services/apiService');
        const response = await apiService.get(`/training/tracks/${trackId}`);
        if (isCancelled) return;
        if (!response?.success || !response?.data) {
          console.error('TrainingSessionRouter: track lookup returned no track', { trackId });
          setDecision('load_failed');
          return;
        }
        setDecision(decideTrainingRoute(response?.data?.trackType));
      } catch (error) {
        console.error('TrainingSessionRouter: track lookup failed', { trackId, error });
        if (!isCancelled) setDecision('load_failed');
      }
    };

    void resolveRoute();
    return () => {
      isCancelled = true;
    };
  }, [trackId, attempt]);

  switch (decision) {
    case 'deciding':
      return (
        <div className="p-12 text-center text-ink-muted">{TRAINING_ROUTER_COPY.preparing}</div>
      );
    case 'playbook_live':
      return <PlaybookLiveSessionPage />;
    case 'load_failed':
      return (
        <RouterNotice
          title={TRAINING_ROUTER_COPY.loadFailedTitle}
          body={TRAINING_ROUTER_COPY.loadFailedBody}
          onRetry={handleRetry}
        />
      );
    default:
      return <TrainingSessionPage />;
  }
};

export default TrainingSessionRouter;
